import { useState } from 'react';
import { db } from '../firebase';
import {
  collection,
  query,
  where,
  getDocs,
  limit,
  doc,
  setDoc,
  Timestamp,
  serverTimestamp,
  runTransaction,
  DocumentReference,
} from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';
import { BookingPeriod } from '../types/types';

interface UseReservationsReturn {
  createInitialReservation: (
    gameId: number,
    day: string,
    bookingPeriod: BookingPeriod
  ) => Promise<{ token: string; expiresAt: string }>;
  confirmReservation: (
    token: string,
    gameId: number,
    day: string,
    name: string,
    email: string,
    isPublicName: boolean,
    homeTeam: string,
    awayTeam: string,
    bookingPeriod: BookingPeriod
  ) => Promise<void>;
  cancelInitialReservation: (token: string) => Promise<void>;
  cancelConfirmedReservation: (
    token: string,
    gameId: number,
    bookingPeriod: BookingPeriod
  ) => Promise<void>;
  cancelReservationAsAdmin: (
    gameId: number,
    bookingPeriod: BookingPeriod
  ) => Promise<void>;
  error: string;
  setError: (error: string) => void;
}

export const useReservations = (): UseReservationsReturn => {
  const [error, setError] = useState<string>('');

  const createInitialReservation = async (
    gameId: number,
    day: string,
    bookingPeriod: BookingPeriod
  ): Promise<{ token: string; expiresAt: string }> => {
    const token = uuidv4();
    const bookingPeriodRef = doc(db, 'booking_periods', bookingPeriod.id);
    const dayDocId = `${bookingPeriod.id}_${day}`;
    const dayDocRef = doc(db, 'days', dayDocId);
    const expiresAt = Timestamp.fromMillis(Date.now() + 60000);

    await runTransaction(db, async (transaction) => {
      const dayDoc = await transaction.get(dayDocRef);

      if (!dayDoc.exists()) {
        transaction.set(dayDocRef, {
          day: day,
          booking_period_id: bookingPeriodRef,
          status: 'PENDING',
          token: token,
          game_id: gameId,
          expires_at: expiresAt,
          reserved_at: serverTimestamp(),
        });
      } else {
        const dayData = dayDoc.data();

        if (dayData.status !== 'OPEN') {
          throw new Error('Day is not available for reservation.');
        }

        transaction.update(dayDocRef, {
          status: 'PENDING',
          token: token,
          game_id: gameId,
          expires_at: expiresAt,
          reserved_at: serverTimestamp(),
        });
      }
    });

    return { token, expiresAt: expiresAt.toDate().toISOString() };
  };

  const confirmReservation = async (
    token: string,
    gameId: number,
    day: string,
    name: string,
    email: string,
    isPublicName: boolean,
    homeTeam: string,
    awayTeam: string,
    bookingPeriod: BookingPeriod
  ): Promise<void> => {
    const periodRef = doc(db, 'booking_periods', bookingPeriod.id);
    const now = Timestamp.now();

    // Find the day document with this token (outside transaction)
    const daysQuery = query(
      collection(db, 'days'),
      where('booking_period_id', '==', periodRef),
      where('token', '==', token),
      where('status', '==', 'PENDING'),
      where('expires_at', '>', now),
      limit(1)
    );

    const daySnapshot = await getDocs(daysQuery);

    if (daySnapshot.empty) {
      throw new Error('Your pending reservation has expired or is invalid.');
    }

    const dayDocRef = daySnapshot.docs[0].ref;

    // Use transaction to ensure atomicity
    await runTransaction(db, async (transaction) => {
      // Re-read the day document inside transaction
      const dayDoc = await transaction.get(dayDocRef);

      if (!dayDoc.exists()) {
        throw new Error('Reservation not found.');
      }

      const dayData = dayDoc.data();

      // Verify token matches
      if (dayData.token !== token) {
        throw new Error('Token is invalid.');
      }

      // Verify status is still PENDING
      if (dayData.status !== 'PENDING') {
        throw new Error('Reservation is no longer pending.');
      }

      // Verify not expired
      if (!dayData.expires_at || dayData.expires_at.toMillis() <= Date.now()) {
        throw new Error('Your pending reservation has expired.');
      }

      // Check booking period is active
      const periodDoc = await transaction.get(periodRef);

      if (!periodDoc.exists()) {
        throw new Error('Booking period does not exist.');
      }

      const periodData = periodDoc.data();
      const bookingOpenAt = periodData.booking_open_at;
      const bookingCloseAt = periodData.booking_close_at;

      if (
        !bookingOpenAt ||
        !bookingCloseAt ||
        bookingOpenAt.toDate() > now.toDate() ||
        bookingCloseAt.toDate() < now.toDate()
      ) {
        throw new Error('Booking period is not currently active.');
      }

      // Verify the day matches
      if (dayData.day !== day) {
        throw new Error('Reservation day mismatch.');
      }

      // Update day document to RESERVED
      transaction.update(dayDocRef, {
        status: 'RESERVED',
        expires_at: null,
        public_name: isPublicName ? name : null,
        reservation_confirmed_at: serverTimestamp(),
        verification_token: token,
      });

      // Create reservation document
      const reservationRef = doc(collection(db, 'reservations'));
      transaction.set(reservationRef, {
        booking_period_id: bookingPeriod.id,
        created_at: serverTimestamp(),
        person_name: name,
        person_email: email,
        public_name: isPublicName,
        day: day,
        homeTeam: homeTeam,
        awayTeam: awayTeam,
        token: token,
        status: 'RESERVED',
      });
    });
  };

  const cancelInitialReservation = async (token: string): Promise<void> => {
    // Find the day document with this token
    const daysQuery = query(
      collection(db, 'days'),
      where('token', '==', token),
      limit(1)
    );

    const daySnapshot = await getDocs(daysQuery);

    if (daySnapshot.empty) {
      throw new Error('Token is invalid.');
    }

    const dayDocRef = daySnapshot.docs[0].ref;

    // Use transaction to verify token still matches
    await runTransaction(db, async (transaction) => {
      const dayDoc = await transaction.get(dayDocRef);

      if (!dayDoc.exists()) {
        throw new Error('Reservation not found.');
      }

      const dayData = dayDoc.data();

      // Verify token matches
      if (dayData.token !== token) {
        throw new Error('Token is invalid.');
      }

      transaction.update(dayDocRef, {
        status: 'OPEN',
        token: null,
        expires_at: null,
        reserved_at: null,
        verification_token: token,
      });
    });
  };

  const cancelConfirmedReservation = async (
    token: string,
    gameId: number,
    bookingPeriod: BookingPeriod
  ): Promise<void> => {
    // Find the day and reservation documents
    const daysQuery = query(
      collection(db, 'days'),
      where('token', '==', token),
      limit(1)
    );

    const daySnapshot = await getDocs(daysQuery);

    if (daySnapshot.empty) {
      throw new Error('Token is invalid.');
    }

    const dayDocRef = daySnapshot.docs[0].ref;

    const reservationQuery = query(
      collection(db, 'reservations'),
      where('token', '==', token),
      limit(1)
    );

    const reservationSnapshot = await getDocs(reservationQuery);

    // Use transaction to verify token and update atomically
    await runTransaction(db, async (transaction) => {
      // READ PHASE: All reads must happen before any writes
      const dayDoc = await transaction.get(dayDocRef);

      if (!dayDoc.exists()) {
        throw new Error('Reservation not found.');
      }

      const dayData = dayDoc.data();

      // Verify token matches
      if (dayData.token !== token) {
        throw new Error('Token is invalid.');
      }

      // Read reservation document if it exists
      let reservationDoc = null;
      let reservationDocRef = null;
      if (!reservationSnapshot.empty) {
        reservationDocRef = reservationSnapshot.docs[0].ref;
        reservationDoc = await transaction.get(reservationDocRef);
      }

      // WRITE PHASE: Now perform all writes
      // Update day document back to OPEN
      transaction.update(dayDocRef, {
        status: 'OPEN',
        expires_at: null,
        reservation_confirmed_at: null,
        token: null,
        verification_token: token,
      });

      // Update the reservation document to CANCELLED
      if (reservationDocRef && reservationDoc && reservationDoc.exists() && reservationDoc.data().token === token) {
        transaction.update(reservationDocRef, {
          status: 'CANCELLED',
          verification_token: token,
        });
      }
    });
  };

  const cancelReservationAsAdmin = async (
    gameId: number,
    bookingPeriod: BookingPeriod
  ): Promise<void> => {
    const currentPeriodRef = doc(db, 'booking_periods', bookingPeriod.id);
    const daysQuery = query(
      collection(db, 'days'),
      where('booking_period_id', '==', currentPeriodRef),
      where('game_id', '==', gameId),
      where('status', '==', 'RESERVED')
    );

    const daySnapshot = await getDocs(daysQuery);

    if (daySnapshot.empty) {
      throw new Error('Reservation not found.');
    }

    const dayDoc = daySnapshot.docs[0];

    // Find reservation document
    const reservationQuery = query(
      collection(db, 'reservations'),
      where('booking_period_id', '==', bookingPeriod.id),
      where('day', '==', dayDoc.data().day),
      where('status', '==', 'RESERVED')
    );

    const reservationSnapshot = await getDocs(reservationQuery);
    
    await runTransaction(db, async (transaction) => {
      // READ PHASE: Read reservation if exists
      let reservationDoc = null;
      if (!reservationSnapshot.empty) {
        reservationDoc = await transaction.get(reservationSnapshot.docs[0].ref);
      }

      // WRITE PHASE: Update documents
      transaction.update(dayDoc.ref, {
        status: 'OPEN',
        expires_at: null,
        reservation_confirmed_at: null,
        token: null,
        public_name: null,
      });

      // Also update the reservation document to CANCELLED
      if (reservationDoc && reservationDoc.exists()) {
        transaction.update(reservationSnapshot.docs[0].ref, {
          status: 'CANCELLED',
        });
      }
    });
  };

  return {
    createInitialReservation,
    confirmReservation,
    cancelInitialReservation,
    cancelConfirmedReservation,
    cancelReservationAsAdmin,
    error,
    setError,
  };
};
